import { MapPin, Bed, Bath, Users, Clock, Zap, Tag, Sparkles, Image as ImageIcon } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useTagSuggestions } from '@/api/tag-suggestions';
import { NoCurrencyMarker } from '@/components/common/NoCurrencyMarker';
import { formatMoney } from '@/lib/format-money';
import type { LocationPreviewData } from '@/types/api';

interface LocationPreviewCardProps {
  preview: LocationPreviewData;
}

function formatPrice(price: number | null, currency: string | null): string {
  if (price === null) return '-';
  return formatMoney(price, currency);
}

export function LocationPreviewCard({ preview }: LocationPreviewCardProps): JSX.Element {
  const title = preview.title?.en ?? preview.title?.de ?? 'Untitled Location';
  const description = preview.description?.en ?? preview.description?.de ?? '';

  // Fetch tag suggestions to get tag names
  const { data: tagSuggestions } = useTagSuggestions(preview.syncedLocationId);

  // Build a map of tagId -> tagName from suggestions
  const tagNameMap = new Map<number, string>();
  if (tagSuggestions?.suggestions.location) {
    for (const tag of tagSuggestions.suggestions.location) {
      tagNameMap.set(tag.tagId, tag.tagName);
    }
  }
  if (tagSuggestions?.suggestions.images) {
    for (const imageTags of Object.values(tagSuggestions.suggestions.images)) {
      for (const tag of imageTags) {
        tagNameMap.set(tag.tagId, tag.tagName);
      }
    }
  }

  // Get confirmed location tag names
  const confirmedTags = (preview.confirmedLocationTags ?? [])
    .map((id) => tagNameMap.get(id))
    .filter((name): name is string => name !== undefined);

  const addressParts = [
    preview.address?.city,
    preview.address?.state,
    preview.address?.country,
  ].filter(Boolean);

  const images = preview.images ?? [];
  const hasImages = images.length > 0;

  // Find the cover image by order matching coverImageIndex, or fall back to first image
  const coverImage = hasImages
    ? (preview.coverImageIndex !== null
        ? images.find((img) => img.order === preview.coverImageIndex)
        : undefined) ?? images[0] ?? null
    : null;

  return (
    <Card className="overflow-hidden" data-testid="location-preview-card">
      {/* Image Section */}
      <div className="relative aspect-video bg-muted">
        {coverImage ? (
          <img
            src={coverImage.url}
            alt={coverImage.caption ?? title}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <div className="text-center text-muted-foreground">
              <ImageIcon className="mx-auto h-12 w-12 opacity-50" />
              <p className="mt-2 text-sm">No images available</p>
            </div>
          </div>
        )}

        {/* Image count badge */}
        {hasImages && (
          <div className="absolute bottom-2 right-2 rounded-md bg-black/60 px-2 py-1 text-xs text-white">
            {images.length} {images.length === 1 ? 'photo' : 'photos'}
          </div>
        )}

        {/* Location type badge */}
        {preview.locationTypeName && (
          <Badge className="absolute left-2 top-2" variant="secondary">
            {preview.locationTypeName}
          </Badge>
        )}
      </div>

      <CardContent className="p-4">
        {/* Title and location */}
        <div className="mb-3">
          <h3 className="text-lg font-semibold leading-tight" data-testid="preview-title">
            {title}
          </h3>
          {addressParts.length > 0 && (
            <div className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
              <MapPin className="h-3.5 w-3.5" />
              {addressParts.join(', ')}
            </div>
          )}
        </div>

        {/* Specs row */}
        <div className="mb-3 flex flex-wrap gap-3 text-sm">
          {preview.bedrooms !== null && (
            <div className="flex items-center gap-1">
              <Bed className="h-4 w-4 text-muted-foreground" />
              <span>{preview.bedrooms} bed</span>
            </div>
          )}
          {preview.bathrooms !== null && (
            <div className="flex items-center gap-1">
              <Bath className="h-4 w-4 text-muted-foreground" />
              <span>{preview.bathrooms} bath</span>
            </div>
          )}
          {(preview.maximumGuests ?? preview.accommodates) !== null && (
            <div className="flex items-center gap-1">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span>{preview.maximumGuests ?? preview.accommodates} guests</span>
            </div>
          )}
        </div>

        {/* Description preview */}
        {description && (
          <p className="mb-3 line-clamp-2 text-sm text-muted-foreground" data-testid="preview-description">
            {description}
          </p>
        )}

        {/* Styles */}
        {(preview.styles?.length ?? 0) > 0 && (
          <div className="mb-3 flex flex-wrap gap-1">
            {preview.styles?.slice(0, 4).map((style) => (
              <Badge key={style} variant="outline" className="text-xs">
                <Tag className="mr-1 h-3 w-3" />
                {style}
              </Badge>
            ))}
            {(preview.styles?.length ?? 0) > 4 && (
              <Badge variant="outline" className="text-xs">
                +{String((preview.styles?.length ?? 0) - 4)} more
              </Badge>
            )}
          </div>
        )}

        {/* AI Tags - Phase 24 (FR-101) */}
        {confirmedTags.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-1" data-testid="preview-ai-tags">
            <Badge variant="secondary" className="text-xs">
              <Sparkles className="mr-1 h-3 w-3" />
              AI Tags
            </Badge>
            {confirmedTags.slice(0, 5).map((tagName) => (
              <Badge key={tagName} variant="outline" className="text-xs">
                {tagName}
              </Badge>
            ))}
            {confirmedTags.length > 5 && (
              <Badge variant="outline" className="text-xs">
                +{String(confirmedTags.length - 5)} more
              </Badge>
            )}
          </div>
        )}

        {/* Pricing */}
        <div className="border-t pt-3">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              {preview.pricing?.pricePerHour !== null && preview.pricing?.pricePerHour !== undefined && (
                <div className="text-sm">
                  <span className="font-semibold">
                    {formatPrice(preview.pricing.pricePerHour, preview.pricing.currency)}
                  </span>
                  {preview.pricing.currency === null && <NoCurrencyMarker />}
                  <span className="text-muted-foreground"> / hour</span>
                </div>
              )}
              {preview.pricing?.pricePerDay !== null && preview.pricing?.pricePerDay !== undefined && (
                <div className="text-sm">
                  <span className="font-semibold">
                    {formatPrice(preview.pricing.pricePerDay, preview.pricing.currency)}
                  </span>
                  {preview.pricing.currency === null && <NoCurrencyMarker />}
                  <span className="text-muted-foreground"> / day</span>
                </div>
              )}
              {(preview.pricing?.pricePerHour === null || preview.pricing?.pricePerHour === undefined) &&
               (preview.pricing?.pricePerDay === null || preview.pricing?.pricePerDay === undefined) && (
                <div className="text-sm text-muted-foreground">Contact for pricing</div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {preview.minimumBookingHours !== null && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  {preview.minimumBookingHours}h min
                </div>
              )}
              {preview.instantBooking && (
                <Badge variant="secondary" className="text-xs">
                  <Zap className="mr-1 h-3 w-3" />
                  Instant
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Features preview */}
        {(preview.features?.length ?? 0) > 0 && (
          <div className="mt-3 border-t pt-3">
            <div className="text-xs text-muted-foreground">
              {preview.features?.slice(0, 5).join(' • ')}
              {(preview.features?.length ?? 0) > 5 && ` • +${String((preview.features?.length ?? 0) - 5)} more`}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
